import { api } from "../api/Middleware";
import { AI_GENERATE_RESPONSE_URL } from "../api/Urls";

// =============================
// AI SERVICES
// =============================

/**
 * Gera resposta usando IA
 * @param {Object} requestData - Dados da requisição
 * @param {string} [requestData.prompt] - Texto/prompt para a IA (obrigatório se não for debug mode)
 * @param {boolean} [requestData.prompt_debug] - Se true, indica modo de debug/análise
 * @param {string} [requestData.debug_prompt] - Prompt específico para debug
 * @param {boolean} [requestData.debug_message] - Se true, indica debug específico de mensagem
 * @param {string|Object} [requestData.conversation_history] - Histórico da conversa para debug
 * @param {string} [requestData.api_key] - Chave da API (opcional se configurada no backend)
 * @param {string} [requestData.model="gpt-4o-mini"] - Modelo da IA
 * @param {number} [requestData.max_tokens=1000] - Máximo de tokens
 * @param {number} [requestData.temperature=0.7] - Temperatura da resposta
 * @param {string} [requestData.system_message] - Mensagem do sistema
 * @returns {Promise<Object>} Resposta da API
 */
export const generateAIResponse = async (requestData) => {
  try {
    // Validar dados obrigatórios - prompt não é obrigatório se debug_prompt for true
    const isDebugMode = requestData.prompt_debug || requestData.debug_prompt;
    if (!isDebugMode && (!requestData.prompt || requestData.prompt.trim() === "")) {
      throw new Error("Prompt é obrigatório");
    }

    // Preparar dados da requisição com valores padrão
    const payload = {
      prompt: requestData.prompt ? requestData.prompt.trim() : "",
      model: requestData.model || "gpt-4.1-mini",
      max_tokens: requestData.max_tokens || 1000,
      temperature: requestData.temperature || 0.7,
      prompt_debug: requestData.prompt_debug || false,
      debug_prompt: requestData.debug_prompt || false,
      debug_message: requestData.debug_message || false,
      system_message: requestData.system_message || "",
      // Incluir conversation_history se fornecido
      ...(requestData.conversation_history && { conversation_history: requestData.conversation_history }),
      // api_key será incluído apenas se fornecido
      ...(requestData.api_key && { api_key: requestData.api_key }),
    };

    const response = await api.post(AI_GENERATE_RESPONSE_URL, payload);

    return {
      success: true,
      data: response.data,
      response: response.data.response,
      model_used: response.data.model_used,
      tokens_used: response.data.tokens_used,
      prompt_tokens: response.data.prompt_tokens,
      completion_tokens: response.data.completion_tokens,
      timestamp: response.data.timestamp,
    };
  } catch (error) {
    console.error("❌ Erro ao gerar resposta da IA:", error);

    // Tratamento de erros específicos
    let errorMessage = "Erro desconhecido ao processar solicitação";
    let errorDetails = null;
    let errorType = "UNKNOWN_ERROR";

    if (error.response?.data) {
      const errorData = error.response.data;

      // Erro 400 - Dados inválidos
      if (error.response.status === 400) {
        errorMessage = errorData.error || "Dados inválidos fornecidos";
        errorType = "VALIDATION_ERROR";
        errorDetails = errorData.details || errorData.error;
      }
      // Erro 500 - Falha na OpenAI
      else if (error.response.status === 500) {
        errorMessage = errorData.error || "Erro interno do servidor de IA";
        errorType = "AI_API_ERROR";
        errorDetails = errorData.details;

        // Mensagem mais amigável para erro da OpenAI
        if (errorData.error === "Erro ao processar solicitação com IA") {
          errorMessage =
            "Não foi possível processar sua solicitação no momento. Tente novamente em alguns instantes.";
        }
      }
      // Outros erros HTTP
      else {
        errorMessage = errorData.error || `Erro HTTP ${error.response.status}`;
        errorType = "HTTP_ERROR";
        errorDetails = errorData.details;
      }
    } else if (error.request) {
      // Erro de rede/conexão
      errorMessage =
        "Não foi possível conectar ao servidor de IA. Verifique sua conexão de internet.";
      errorType = "CONNECTION_ERROR";
    } else if (error.message === "Prompt é obrigatório") {
      // Erro de validação local
      errorMessage = error.message;
      errorType = "VALIDATION_ERROR";
    } else {
      // Outros erros
      errorMessage = error.message || errorMessage;
      errorType = "LOCAL_ERROR";
    }

    return {
      success: false,
      error: errorMessage,
      errorType: errorType,
      errorDetails: errorDetails,
      response: null,
      model_used: null,
      tokens_used: 0,
      prompt_tokens: 0,
      completion_tokens: 0,
      timestamp: new Date().toISOString(),
    };
  }
};

/**
 * Gera resposta da IA para preenchimento de campos de agentes
 * @param {Object} params - Parâmetros específicos para agentes
 * @param {string} params.prompt - Prompt customizado ou texto base
 * @param {string} [params.agentName] - Nome do agente
 * @param {string} [params.businessArea] - Área de atuação do negócio
 * @param {string} [params.personality] - Personalidade do agente
 * @param {string} [params.stepType] - Tipo da etapa (greeting, presentation, etc.)
 * @param {string} [params.stepDescription] - Descrição da etapa
 * @returns {Promise<Object>} Resposta da API
 */
export const generateAgentFieldContent = async (params) => {
  try {
    // Construir prompt inteligente baseado nos parâmetros
    let enhancedPrompt = params.prompt;

    // Se não há prompt customizado, criar um baseado nos parâmetros
    if (!enhancedPrompt || enhancedPrompt.trim() === "") {
      enhancedPrompt = buildAgentPrompt(params);
    }

    // Mensagem do sistema específica para agentes
    const systemMessage = `Você é um especialista em criação de agentes de IA para WhatsApp. 
Crie textos naturais, humanizados e diretos ao ponto.
Os textos devem ser apropriados para WhatsApp (sem formatação markdown).
Seja conciso e use uma linguagem amigável e profissional.
Retorne apenas o texto que o agente deve falar, sem explicações adicionais.`;

    return await generateAIResponse({
      prompt: enhancedPrompt,
      system_message: systemMessage,
      model: "gpt-4.1-mini",
      max_tokens: 500,
      temperature: 0.8, // Um pouco mais criativo para textos humanizados
    });
  } catch (error) {
    console.error("❌ Erro ao gerar conteúdo para agente:", error);
    return {
      success: false,
      error: "Erro ao gerar conteúdo para o agente",
      errorType: "AGENT_CONTENT_ERROR",
      errorDetails: error.message,
    };
  }
};

/**
 * Constrói um prompt inteligente baseado nos parâmetros do agente
 * @private
 */
const buildAgentPrompt = (params) => {
  const { agentName, businessArea, personality, stepType, stepDescription } =
    params;

  let prompt = "Crie um texto para um agente de IA";

  // Adicionar informações do agente
  if (agentName) {
    prompt += ` chamado ${agentName}`;
  }

  if (businessArea) {
    prompt += ` que atua na área de ${businessArea}`;
  }

  if (personality) {
    prompt += ` com personalidade ${personality}`;
  }

  // Adicionar contexto da etapa
  if (stepType && stepDescription) {
    prompt += `.\n\nEsta é uma etapa de "${stepType}": ${stepDescription}`;
  } else if (stepDescription) {
    prompt += `.\n\nContexto: ${stepDescription}`;
  }

  // Adicionar instruções finais
  prompt += `\n\nO texto deve ser:
- Natural e humanizado
- Direto ao ponto
- Apropriado para WhatsApp
- Sem formatação markdown
- Profissional mas amigável

Retorne apenas o texto que o agente deve falar.`;

  return prompt;
};

export default {
  generateAIResponse,
  generateAgentFieldContent,
};
